import { GlobalHelper } from "./helpers/global-helper";
import {
  updateMessageClickStatus,
  updateMessageReadStatus,
} from "../modules/onboarding/network/network";
import { registerPushNotification } from "../modules/notification/redux/notification-slice";
import { confirm } from "../components/modal/comfirm";
import { debounce } from "lodash";
import { getI18n } from "react-i18next";
import { SESSION_STORAGE_KEY } from "./session-storage";
import { isBrowserSupported } from "../firebase-config";
import { LOCAL_STORAGE_KEY } from "./local-storage";

export interface ExtendData {
  redirectTo: "app" | "url";
  link: string;
  openBy: "external" | null;
  params?: Record<string, any>;
}

export interface NotificationItem {
  id?: string;
  extendValue: ExtendData | string;
}

interface HandleNotificationParams {
  item: NotificationItem;
  dispatchCb?: (id: string) => Promise<void>;
}

export const onHandleNotification = async ({ item, dispatchCb }: HandleNotificationParams) => {
  try {
    if (item.id && dispatchCb) {
      await dispatchCb(item.id);
    }

    const extendValue: ExtendData =
      typeof item.extendValue === "string" ? JSON.parse(item.extendValue) : item.extendValue;

    if (!extendValue || !extendValue.link) {
      console.warn("Invalid extendValue or link");
      return;
    }

    const params =
      typeof extendValue.params === "string" ? JSON.parse(extendValue.params) : extendValue.params;

    switch (extendValue.redirectTo) {
      case "app": {
        const navigate = GlobalHelper.getGlobalNavigate();
        const routesMapping: Record<string, string> = {
          recruitmentcandidateprofile: "/web/onboarding/candidate-home",
          inboxdetail: "/web/onboarding/inbox",
        };
        const route = routesMapping[extendValue.link];
        if (route) {
          navigate(route, { state: params });
        } else {
          console.warn(`Route mapping not found: ${extendValue.link}`);
        }
        break;
      }
      case "url":
        if (extendValue.openBy === "external") {
          window.open(extendValue.link, "_blank");
        } else {
          window.location.href = extendValue.link;
        }
        break;
      default:
        console.warn(`Unsupported redirectTo type: ${extendValue.redirectTo}`);
    }
  } catch (error) {
    console.error("Failed to handle notification:", error);
  }
};

export const defaultDispatchCb = async (id: string) => {
  await Promise.all([updateMessageClickStatus(id), updateMessageReadStatus(id)]).catch((e) =>
    console.error("Failed to update message status:", e),
  );
};

const showNotificationConfirmDialog = debounce(
  async (): Promise<boolean> => {
    const t = getI18n().t;
    return new Promise<boolean>((resolve) => {
      const dayDuration = 7;
      confirm(
        {
          title: t("Common.turn_on_notification"),
          message: t("Common.turn_on_notification_reminder_desc"),
          buttons: [
            {
              text: t("global.text.confirm"), //PHKL Custom
              onClick: () => {
                resolve(true);
              },
              colorType: "primary",
            },
            {
              text: t("Common.remind_me_x_days_later", { n: dayDuration }),
              onClick: () => {
                resolve(false);
                localStorage.setItem(
                  LOCAL_STORAGE_KEY.REMIND_ME_LATER_NOTIFICATION_TS,
                  (Date.now() + dayDuration * 24 * 60 * 60 * 1000).toString(),
                );
              },
              colorType: "secondary",
            },
            {
              text: t("Common.close"),
              onClick: () => {
                resolve(false);
                localStorage.setItem(
                  LOCAL_STORAGE_KEY.REMIND_ME_LATER_NOTIFICATION_TS,
                  // Set to a very very distant future timestamp to avoid showing the dialog again
                  (Date.now() + 100 * 365 * 24 * 60 * 60 * 1000).toString(),
                );
              },
              colorType: "secondary",
            },
          ],
        },
        { backdropClose: false, id: "" },
      );
    });
  },
  300,
  { leading: true, trailing: false },
);

const _registerDeviceForPushNotification = async ({
  dispatch,
  resolveCb,
  rejectCb,
  showCustomConfirm = false,
}: {
  dispatch: any;
  resolveCb?: () => void;
  rejectCb?: () => void;
  showCustomConfirm?: boolean;
}) => {
  try {
    if (!isBrowserSupported()) {
      console.warn("Push notifications are not supported in this browser");
      return;
    }

    let permission = Notification.permission;

    if (permission === "default") {
      if (showCustomConfirm) {
        const confirmed = await showNotificationConfirmDialog();
        if (!confirmed) {
          rejectCb?.();
          return;
        }
      }

      // Record start time
      const startTime = performance.now();
      permission = await Notification.requestPermission();
      // Record end time and calculate duration
      const endTime = performance.now();
      const duration = endTime - startTime;
      // If permission is denied and duration is very short, assume lack of user interaction
      const INTERACTION_THRESHOLD_MS = 500; // Adjust based on testing

      if (
        duration < INTERACTION_THRESHOLD_MS &&
        permission === "denied" &&
        Notification.permission === "default" &&
        !showCustomConfirm
      ) {
        // Important Note: If the permission is denied immediately after requesting, it might be due to a lack of user interaction.
        // In this case, we can show a custom confirmation dialog to ask the user again.
        await _registerDeviceForPushNotification({
          dispatch,
          resolveCb,
          rejectCb,
          showCustomConfirm: true,
        });
        return;
      }
    }

    if (Notification.permission === "denied") {
      rejectCb?.();
      return;
    }

    if (permission === "granted") {
      dispatch(registerPushNotification());
      resolveCb?.();
    }
  } catch (error) {
    console.error("Permission request failed:", error);
    rejectCb?.();
  }
};

export const registerDeviceForPushNotification = debounce(_registerDeviceForPushNotification, 300, {
  leading: true,
  trailing: false,
});
